import { CalendarPlus, ChatText, FileAudio, FileDoc, FileImage, FileVideo, WarningCircle } from "@phosphor-icons/react";
import { DialogProps } from "../../Dialog";
import { Modal } from "@void-orbit/ui";
import { MessageTypesView } from "../styles";
import { useStore } from "../../../store";
import { IconButton } from "../../Buttons/IconButton";
import { Form } from "../../Form";
import { useShipmentModalController } from "../../../controllers/modals/shipmentModalController";
import { Button, Size, Variant } from "../../Buttons";
import { Shipment, ShipmentMessageType, ShipmentStatus } from "../../../models/Shipment";
import { AnimatePresence, motion } from "motion/react";
import { TextField } from "../../Form/Fields/TextField";
import { TextMessageInput } from "../../Form/Fields/TextMessageInput";
import { Controller } from "react-hook-form";
import { ContactItem, InputFileMessage, SelectedContact } from "./styles";
import { AsyncAutocomplete } from "../../Form/Fields/AsyncAutocomplete";
import { Contact } from "../../../models/Contact";
import { Avatar } from "../../Avatar";
import { searchContacts, SearchContactsPayload } from "../../../services/contactServices";
import { ShipmentForm } from "../../../schemas/shipmentSchema";
import { buildAttachmentFileURL, buildInlineFileURL } from "../../../utils/fileProxy";
import { useFileAccessURL } from "../../../hooks/useFileAccessURL";
import { FileAccessMediaKind, getFileAccessReferences } from "../../../utils/fileAccessURL";

const messageTypesArray = [
  {
    messageType: ShipmentMessageType.TEXT,
    Icon: ChatText,
    label: "Texto",
  },
  {
    messageType: ShipmentMessageType.IMAGE,
    Icon: FileImage,
    label: "Imagem",
  },
  {
    messageType: ShipmentMessageType.AUDIO,
    Icon: FileAudio,
    label: "Áudio",
  },
  {
    messageType: ShipmentMessageType.VIDEO,
    Icon: FileVideo,
    label: "Vídeo",
  },
  {
    messageType: ShipmentMessageType.DOCUMENT,
    Icon: FileDoc,
    label: "Documento",
  },
];

export interface ShipmentModalProps extends Omit<DialogProps, "children">{
  shipment: Shipment | null;
};

export const ShipmentModal = ({
  onClose = () => {},
  open,
  shipment,
}: ShipmentModalProps ) => {
  const {
    theme,
  } = useStore();

  const {
    form: {
      register,
      watch,
      control,
      formState: {
        errors,
        isValid,
      }
    },
    showMessage,
    multipartUploadState,
    multipartEnabled,
    handleSubmit,
    toggleShowMessage,
    handleDelete,
    abortUpload,
  } = useShipmentModalController( shipment, open, onClose );
  const shipmentIsActive = Boolean( shipment ) && shipment?.deletedAt == null;
  const shipmentMediaKind: FileAccessMediaKind = shipment?.shipmentMessageType === ShipmentMessageType.IMAGE
    ? "image"
    : shipment?.shipmentMessageType === ShipmentMessageType.VIDEO
      ? "video"
      : shipment?.shipmentMessageType === ShipmentMessageType.AUDIO
        ? "audio"
        : "document";
  const shipmentFileReference = shipment?.fileMetadata ?? { url: shipment?.url };
  const shipmentInlineReference = getFileAccessReferences(shipmentFileReference, "inline", shipmentMediaKind)[0];
  const shipmentAttachmentReference = getFileAccessReferences(shipmentFileReference, "attachment", shipmentMediaKind)[0];
  const shipmentInlineURL = useFileAccessURL({
    mode: "inline",
    fileMetadataId: shipment?.fileMetadata?.id,
    reference: shipmentInlineReference,
  });
  const shipmentAttachmentURL = useFileAccessURL({
    mode: "attachment",
    fileMetadataId: shipment?.fileMetadata?.id,
    reference: shipmentAttachmentReference,
  });

  const renderFileInput = ( shipmentMessageType: ShipmentMessageType ) => {
    const fileAccpetMapper: {
      [ key in ShipmentMessageType ]: string;
    } = {
      DOCUMENT: "*",
      AUDIO: "audio/*",
      IMAGE: "image/*",
      VIDEO: "video/*",
      TEXT: "*",
    };

    return (
      <Controller
        control={ control }
        name="file"
        render={({ field, fieldState: { error }}) => (
          <>
            <InputFileMessage>
              {
                field.value
                  ? <span>{ field.value.name }</span>
                  : <span style={{justifySelf: "center"}}>Clique para selecionar um arquivo</span>
              }

              <input
                type="file"
                onChange={(e) => {
                  const file = e.target.files?.[0] ?? null;
                  field.onChange(file);
                }}
                accept={ fileAccpetMapper[ shipmentMessageType ]}
                multiple={ false }
                id="shipment-file-input"
              />
            </InputFileMessage>

            {
              multipartEnabled && field.value && (
                <div style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: theme.spacing.xxx_small,
                  marginTop: theme.spacing.xxx_small,
                }}>
                  <span>
                    {
                      multipartUploadState.status === "COMPLETED"
                        ? "Upload concluido"
                        : multipartUploadState.status === "FAILED"
                          ? multipartUploadState.error ?? "Falha no upload"
                          : `Upload ${ multipartUploadState.percentage }% (${ multipartUploadState.currentPart }/${ multipartUploadState.totalParts || 1 })`
                    }
                  </span>

                  {
                    ["INITIATING", "UPLOADING", "COMPLETING", "FAILED"].includes( multipartUploadState.status ) && (
                      <button
                        type="button"
                        onClick={() => {
                          field.onChange(null);
                          void abortUpload();
                        }}
                        style={{
                          border: "none",
                          background: "transparent",
                          color: theme.colors.error.default,
                          cursor: "pointer",
                        }}
                      >
                        Remover
                      </button>
                    )
                  }
                </div>
              )
            }

            <span className="input-error-message">{ error?.message ?? ""}</span>
          </>
        )}
      />
    );
  };

  const renderContentInput = () => {
    return (
      <Controller
        control={ control }
        name="content"
        render={({ field, fieldState: { error }}) => (
          <>
            <TextMessageInput
              value={ field.value ?? ""}
              onChange={ field.onChange }
            />

            <span className="input-error-message">{ error?.message ?? ""}</span>
          </>
        )}
      />
    );
  };

  const getMessageInputByType = ( shipmentMessageType: ShipmentMessageType ) => {
    const messageComponent: {
      [ key in ShipmentMessageType ]: React.ReactElement;
    } = {
      TEXT:
        <div className="input-view">
          <label className="input-label">Mensagem de texto</label>

          { renderContentInput()}
        </div>,
      AUDIO:
        <div className="input-view">
          <label className="input-label">Arquivo de áudio</label>

          { renderFileInput( ShipmentMessageType.AUDIO )}
        </div>,
      IMAGE:
        <>
          <div className="input-view">
            <label className="input-label">Arquivo de imagem</label>

            { renderFileInput( ShipmentMessageType.IMAGE )}
          </div>

          <div className="input-view">
            <label className="input-label">Legenda da imagem</label>

            { renderContentInput()}
          </div>
        </>,
      VIDEO:
        <>
          <div className="input-view">
            <label className="input-label">Arquivo de vídeo</label>

            { renderFileInput( ShipmentMessageType.VIDEO )}
          </div>

          <div className="input-view">
            <label className="input-label">Legenda do vídeo</label>

            { renderContentInput()}
          </div>
        </>,
      DOCUMENT:
        <>
          <div className="input-view">
            <label className="input-label">Arquivo de documento</label>

            { renderFileInput( ShipmentMessageType.DOCUMENT )}
          </div>

          <div className="input-view">
            <label className="input-label">Legenda do documento</label>

            { renderContentInput()}
          </div>
        </>,
    };

    return messageComponent[ shipmentMessageType ];
  };

  return (
    <Modal
      open={ open }
      onClose={ onClose }
      title={ `${ shipment?.id ? "Editar" : "Novo" } Agendamento` }
      closeLabel="Fechar envio agendado"
      icon={
        <CalendarPlus
          weight="duotone"
          color={ theme.colors.text.default }
          size={ 32 }
        />
      }
      footer={
        showMessage.delete
          ? {
              color: "error",
              message: (
                <>
                  <WarningCircle
                    size={ 16 }
                    weight="duotone"
                  />

                  Deseja realmente cancelar este envio?
                </>
              ),
              actions: (
                <>
                  <Button
                    $size={ Size.SMALL }
                    $color="error"
                    $variant={ Variant.OUTLINED }
                    onClick={() => toggleShowMessage("delete")}
                  >
                    Não
                  </Button>

                  <Button
                    $size={ Size.SMALL }
                    $color="error"
                    onClick={ handleDelete }
                  >
                    Sim
                  </Button>
                </>
              ),
            }
          : null
      }
      onFooterDismiss={() => toggleShowMessage("delete")}
    >
      <Form
        onSubmit={ handleSubmit }
      >
        <div className="inputs-view">
          <AsyncAutocomplete<Contact, SearchContactsPayload, ShipmentForm>
            control={ control }
            label="Contato"
            name="contact.id"
            placeholder="Selecione o contato"
            onSearch={ searchContacts }
            prefetchedSelectedOption={ shipment?.contact ?? null }
            renderSelected={( contact ) => (
              <SelectedContact>
                <Avatar
                  name={ contact.name }
                  url={ buildInlineFileURL( contact.avatar )}
                  size={ 28 }
                />

                <div className="info-view">
                  <span>{ contact.name }</span>
                  <span>{ contact.phone }</span>
                </div>
              </SelectedContact>
            )}
            renderItem={( contact ) => (
              <ContactItem>
                <Avatar
                  name={ contact.name }
                  url={ buildInlineFileURL( contact.avatar )}
                  size={ 32 }
                />

                <div className="info-view">
                  <span>{ contact.name }</span>
                  <span>{ contact.phone }</span>
                </div>
              </ContactItem>
            )}
          />

          <TextField
            registerProps={ register("title")}
            label="Título"
            placeholder="Título"
            error={ errors.title }
          />

          <Controller
            control={ control }
            name="shipmentMessageType"
            render={({ field }) => (
              <div className="input-view">
                <label className="input-label">Tipo de mensagem</label>

                <MessageTypesView role="group" aria-label="Tipo de mensagem">
                  {
                    messageTypesArray.map(({ messageType, Icon, label } ) => (
                      <IconButton
                        key={ messageType }
                        color={ field.value === messageType ? "info" : "text"}
                        onClick={() => field.onChange(messageType)}
                        aria-label={ label }
                        aria-pressed={ field.value === messageType }
                      >
                        <Icon
                          size={ 24 }
                        />
                      </IconButton>
                    ))
                  }
                </MessageTypesView>
              </div>
            )}
          />

          { getMessageInputByType( watch("shipmentMessageType") ?? ShipmentMessageType.TEXT )}

          {
            shipment?.url && (
              <div className="input-view">
                <label className="input-label">Anexo atual</label>

                <div style={{
                  display: "flex",
                  gap: theme.spacing.xx_small,
                  flexWrap: "wrap",
                }}>
                  <a
                    href={ shipmentInlineURL || buildInlineFileURL( shipmentInlineReference )}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Visualizar
                  </a>

                  <a
                    href={ shipmentAttachmentURL || buildAttachmentFileURL( shipmentAttachmentReference )}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Baixar
                  </a>
                </div>
              </div>
            )
          }

          <div className="inputs-view-row">
            <TextField
              registerProps={ register("sendDate")}
              label="Data de envío"
              placeholder="Data de envío"
              type="date"
              error={ errors.sendDate }
            />

            <TextField
              registerProps={ register("sendTime")}
              label="Hora de envío"
              placeholder="Hora de envío"
              type="time"
              error={ errors.sendTime }
            />
          </div>
        </div>

        <div className="actions-view">
          <div className="actions-view-row">
            {
              shipmentIsActive && shipment?.status === ShipmentStatus.PENDING
                ? (
                    <>
                      <div>
                        <AnimatePresence>
                          {
                            !showMessage.delete && (
                              <motion.div
                                initial={{
                                  scale: 0,
                                }}
                                animate={{
                                  scale: 1,
                                }}
                                exit={{
                                  scale: 0,
                                }}
                                transition={{ duration: .2 }}
                              >
                                <Button
                                  $color="error"
                                  $variant={ Variant.TEXT }
                                  onClick={() => toggleShowMessage("delete")}
                                >
                                  Excluir
                                </Button>
                              </motion.div>
                            )
                          }
                        </AnimatePresence>
                      </div>
                    </>
                  )
                : <div />
            }

            <Button
              $color="text"
              $variant={ Variant.CONTAINED }
              type="submit"
              disabled={ !isValid || ["INITIATING", "UPLOADING", "COMPLETING"].includes( multipartUploadState.status ) }
            >
              Salvar
            </Button>
          </div>
        </div>
      </Form>
    </Modal>
  );
};
